// src/context/BackupContext.js
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useAuth } from './AuthContext';
import backupScheduler from '../services/BackupScheduler';
import backupService from '../services/BackupService';
import { toast } from 'react-hot-toast';

/**
 * Contexte pour gérer le système de sauvegarde dans l'application
 * Intègre le scheduler automatique et fournit une API React
 */
const BackupContext = createContext();

export const useBackup = () => {
  const context = useContext(BackupContext);
  if (!context) {
    throw new Error('useBackup must be used within a BackupProvider');
  }
  return context;
};

export const BackupProvider = ({ children }) => {
  const { user } = useAuth();
  const [isSchedulerRunning, setIsSchedulerRunning] = useState(false);
  const [schedulerStats, setSchedulerStats] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [backupHistory, setBackupHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Refs pour éviter les re-renders inutiles
  const schedulerSubscriptionRef = useRef(null);
  const statsIntervalRef = useRef(null);

  // Initialisation du contexte au montage
  useEffect(() => {
    if (user && isAdminUser(user)) {
      initializeBackupSystem();
    }

    return () => {
      cleanup();
    };
  }, [user]);

  /**
   * Vérifier si l'utilisateur est admin
   */
  const isAdminUser = (user) => {
    return user?.role === 'super_admin' || user?.role === 'admin';
  };

  /**
   * Initialiser le système de sauvegarde
   */
  const initializeBackupSystem = async () => {
    console.log('🚀 Initialisation du système de sauvegarde');
    
    try {
      setIsLoading(true);
      setError(null);

      // Charger l'historique des sauvegardes
      await loadBackupHistory();

      // S'abonner aux notifications du scheduler
      subscribToSchedulerNotifications();

      // Démarrer le monitoring des statistiques
      startStatsMonitoring();

      // Auto-démarrer le scheduler si configuré
      const shouldAutoStart = localStorage.getItem('backupSchedulerAutoStart') !== 'false';
      if (shouldAutoStart) {
        await startScheduler();
      }

      console.log('✅ Système de sauvegarde initialisé');

    } catch (err) {
      console.error('❌ Erreur initialisation sauvegarde:', err);
      setError(`Erreur d'initialisation: ${err.message}`);
      
      // Toast d'erreur
      toast.error('Erreur lors de l\'initialisation du système de sauvegarde');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Charger l'historique des sauvegardes
   */
  const loadBackupHistory = async () => {
    try {
      const backups = await backupService.listBackups({ limit: 50 });
      setBackupHistory(backups);
    } catch (err) {
      console.error('❌ Erreur chargement historique:', err);
      throw err;
    }
  };

  /**
   * S'abonner aux notifications du scheduler
   */
  const subscribToSchedulerNotifications = () => {
    if (schedulerSubscriptionRef.current) {
      schedulerSubscriptionRef.current(); // Nettoyer l'ancien abonnement
    }

    schedulerSubscriptionRef.current = backupScheduler.subscribe((notification) => {
      console.log('📢 Notification scheduler:', notification);
      
      // Ajouter à la liste des notifications
      setNotifications(prev => [notification, ...prev.slice(0, 49)]); // Garder max 50 notifications
      
      // Afficher un toast selon le type
      switch (notification.type) {
        case 'success':
          toast.success(notification.title, {
            description: notification.message,
            duration: 5000
          });
          break;
          
        case 'error':
          toast.error(notification.title, {
            description: notification.message,
            duration: 8000
          });
          break;
          
        case 'warning':
          toast.warning(notification.title, {
            description: notification.message,
            duration: 6000
          });
          break;
          
        case 'info':
          toast(notification.title, {
            description: notification.message,
            duration: 4000,
            icon: '📢'
          });
          break;
      }

      // Recharger l'historique après une sauvegarde réussie
      if (notification.type === 'success' && notification.title.includes('sauvegarde')) {
        loadBackupHistory();
      }
    });
  };

  /**
   * Démarrer le monitoring des statistiques
   */
  const startStatsMonitoring = () => {
    if (statsIntervalRef.current) {
      clearInterval(statsIntervalRef.current);
    }

    // Mettre à jour les stats toutes les 30 secondes
    statsIntervalRef.current = setInterval(() => {
      const stats = backupScheduler.getStats();
      setSchedulerStats(stats);
      setIsSchedulerRunning(stats.isRunning);
    }, 30000);

    // Première mise à jour immédiate
    const stats = backupScheduler.getStats();
    setSchedulerStats(stats);
    setIsSchedulerRunning(stats.isRunning);
  };

  /**
   * Démarrer le scheduler
   */
  const startScheduler = async () => {
    try {
      console.log('🚀 Démarrage du scheduler de sauvegarde');
      
      backupScheduler.start();
      setIsSchedulerRunning(true);
      
      // Sauvegarder la préférence
      localStorage.setItem('backupSchedulerAutoStart', 'true');
      
      toast.success('Scheduler de sauvegarde démarré', {
        description: 'Les sauvegardes automatiques sont maintenant actives'
      });
      
    } catch (err) {
      console.error('❌ Erreur démarrage scheduler:', err);
      setError(`Erreur démarrage: ${err.message}`);
      toast.error('Erreur lors du démarrage du scheduler');
      throw err;
    }
  };

  /**
   * Arrêter le scheduler
   */
  const stopScheduler = async () => {
    try {
      console.log('🛑 Arrêt du scheduler de sauvegarde');
      
      backupScheduler.stop();
      setIsSchedulerRunning(false);
      
      // Sauvegarder la préférence
      localStorage.setItem('backupSchedulerAutoStart', 'false');
      
      toast.warning('Scheduler de sauvegarde arrêté', {
        description: 'Les sauvegardes automatiques ont été désactivées'
      });
      
    } catch (err) {
      console.error('❌ Erreur arrêt scheduler:', err);
      setError(`Erreur arrêt: ${err.message}`);
      toast.error('Erreur lors de l\'arrêt du scheduler');
      throw err;
    }
  };

  /**
   * Redémarrer le scheduler
   */
  const restartScheduler = async () => {
    try {
      console.log('🔄 Redémarrage du scheduler');
      
      backupScheduler.restart();
      
      toast.info('Scheduler redémarré', {
        description: 'Le scheduler de sauvegarde a été redémarré'
      });
      
    } catch (err) {
      console.error('❌ Erreur redémarrage scheduler:', err);
      setError(`Erreur redémarrage: ${err.message}`);
      toast.error('Erreur lors du redémarrage du scheduler');
      throw err;
    }
  };

  /**
   * Déclencher une sauvegarde manuelle
   */
  const triggerManualBackup = async (type = 'manual') => {
    try {
      console.log(`🔧 Déclenchement sauvegarde manuelle (${type})`);
      setIsLoading(true);
      
      toast.loading('Création de la sauvegarde...', {
        id: 'manual-backup'
      });
      
      await backupScheduler.triggerManualBackup(type);
      
      toast.success('Sauvegarde manuelle terminée', {
        id: 'manual-backup',
        description: 'La sauvegarde a été créée avec succès'
      });
      
    } catch (err) {
      console.error('❌ Erreur sauvegarde manuelle:', err);
      setError(`Erreur sauvegarde: ${err.message}`);
      
      toast.error('Erreur lors de la sauvegarde', {
        id: 'manual-backup',
        description: err.message
      });
      
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Créer une sauvegarde avec options personnalisées
   */
  const createCustomBackup = async (options = {}) => {
    try {
      console.log('🎛️ Création sauvegarde personnalisée:', options);
      setIsLoading(true);
      
      toast.loading('Création de la sauvegarde personnalisée...', {
        id: 'custom-backup'
      });
      
      const result = await backupService.createFullBackup({
        userId: user.uid,
        type: 'custom',
        ...options
      });
      
      // Recharger l'historique
      await loadBackupHistory();
      
      toast.success('Sauvegarde personnalisée terminée', {
        id: 'custom-backup',
        description: `Sauvegarde ${result.id} créée avec succès`
      });
      
      return result;
      
    } catch (err) {
      console.error('❌ Erreur sauvegarde personnalisée:', err);
      setError(`Erreur sauvegarde: ${err.message}`);
      
      toast.error('Erreur lors de la sauvegarde personnalisée', {
        id: 'custom-backup',
        description: err.message
      });
      
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Supprimer une sauvegarde
   */
  const deleteBackup = async (backupId) => {
    try {
      console.log(`🗑️ Suppression sauvegarde: ${backupId}`);
      
      await backupService.deleteBackup(backupId);
      
      // Recharger l'historique
      await loadBackupHistory();
      
      toast.success('Sauvegarde supprimée', {
        description: `Sauvegarde ${backupId} supprimée avec succès`
      });
      
    } catch (err) {
      console.error('❌ Erreur suppression:', err);
      setError(`Erreur suppression: ${err.message}`);
      toast.error('Erreur lors de la suppression');
      throw err;
    }
  };

  /**
   * Restaurer depuis une sauvegarde
   */
  const restoreFromBackup = async (backupId, options = {}) => {
    try {
      console.log(`🔄 Restauration depuis: ${backupId}`, options);
      setIsLoading(true);
      
      toast.loading('Restauration en cours...', {
        id: 'restore-backup'
      });
      
      const result = await backupService.restoreFromBackup(backupId, {
        userId: user.uid,
        ...options
      });
      
      toast.success('Restauration terminée', {
        id: 'restore-backup',
        description: 'Les données ont été restaurées avec succès'
      });
      
      return result;
      
    } catch (err) {
      console.error('❌ Erreur restauration:', err);
      setError(`Erreur restauration: ${err.message}`);
      
      toast.error('Erreur lors de la restauration', {
        id: 'restore-backup',
        description: err.message
      });
      
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Effacer les notifications
   */
  const clearNotifications = () => {
    setNotifications([]);
    toast.success('Notifications effacées');
  };

  /**
   * Effacer l'erreur
   */
  const clearError = () => {
    setError(null);
  };

  /**
   * Actualiser les données
   */
  const refresh = async () => {
    try {
      setIsLoading(true);
      await loadBackupHistory();
      
      const stats = backupScheduler.getStats();
      setSchedulerStats(stats);
      setIsSchedulerRunning(stats.isRunning);
      
      toast.success('Données actualisées');
      
    } catch (err) {
      console.error('❌ Erreur actualisation:', err);
      toast.error('Erreur lors de l\'actualisation');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Nettoyer les ressources
   */
  const cleanup = () => {
    if (schedulerSubscriptionRef.current) {
      schedulerSubscriptionRef.current();
      schedulerSubscriptionRef.current = null;
    }
    
    if (statsIntervalRef.current) {
      clearInterval(statsIntervalRef.current);
      statsIntervalRef.current = null;
    }
  };

  // Valeurs et méthodes exposées par le contexte
  const contextValue = {
    // États
    isSchedulerRunning,
    schedulerStats,
    notifications,
    backupHistory,
    isLoading,
    error,
    
    // Actions du scheduler
    startScheduler,
    stopScheduler,
    restartScheduler,
    
    // Actions de sauvegarde
    triggerManualBackup,
    createCustomBackup,
    deleteBackup,
    restoreFromBackup,
    
    // Utilitaires
    clearNotifications,
    clearError,
    refresh,
    
    // Services directs
    backupService,
    backupScheduler
  };

  return (
    <BackupContext.Provider value={contextValue}>
      {children}
    </BackupContext.Provider>
  );
};

export default BackupContext;